import {
  TERMINOLOGY_CONTROLLED_SELECTOR,
  TerminologyRenderIdentity,
} from './terminology-render-identity';

const MAX_RETURN_URL_LENGTH = 2048;
const MAX_USAGE_ID_LENGTH = 240;
const FOCUS_OWNER_SELECTOR = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[role="button"]',
  '[role="columnheader"]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

export type TerminologyContextualWorkspaceView = 'technical' | 'edit' | 'impact';

/** Builds the one exact handoff contract shared by pointer, keyboard, and nearby actions. */
export function createTerminologyContextualQueryParams(
  identity: TerminologyRenderIdentity,
  currentUrl: string | null | undefined,
  view: TerminologyContextualWorkspaceView,
): Record<string, string> {
  const queryParams: Record<string, string> = {
    token: identity.tokenCode,
    configuration: identity.configurationCode,
    usage: identity.usageId,
    view,
  };
  const returnUrl = normalizeTerminologyReturnUrl(currentUrl);
  if (returnUrl) {
    queryParams['returnUrl'] = returnUrl;
    queryParams['returnUsage'] = identity.usageId;
  }

  addQueryValue(queryParams, 'sourceLocale', identity.localeCode);
  addQueryValue(queryParams, 'sourceJurisdiction', identity.jurisdictionCode);
  addQueryValue(queryParams, 'sourceProgram', identity.programCode);
  addQueryValue(queryParams, 'sourceChannel', identity.channelCode);
  addQueryValue(queryParams, 'sourceAudience', identity.audienceCode);
  addQueryValue(queryParams, 'sourceRevision', identity.mapRevision, 128);
  return queryParams;
}

/**
 * Accepts only an application-relative return target. Contextual terminology links may be copied,
 * refreshed, or manually edited, so the destination is treated as untrusted route input.
 */
export function normalizeTerminologyReturnUrl(value: string | null | undefined): string | null {
  const candidate = value?.trim() ?? '';
  if (
    candidate.length === 0
    || candidate.length > MAX_RETURN_URL_LENGTH
    || !candidate.startsWith('/')
    || candidate.startsWith('//')
    || candidate.includes('\\')
    || containsAsciiControlCharacter(candidate)
  ) {
    return null;
  }

  return candidate;
}

/** Keeps a durable usage identity bounded before displaying it or using it for focus recovery. */
export function normalizeTerminologyReturnUsage(value: string | null | undefined): string | null {
  return normalizeTerminologyContextValue(value, MAX_USAGE_ID_LENGTH);
}

/** Bounds non-secret runtime context carried through a contextual terminology route. */
export function normalizeTerminologyContextValue(
  value: string | null | undefined,
  maximumLength = 120,
): string | null {
  const candidate = value?.trim() ?? '';
  return candidate.length > 0
    && candidate.length <= maximumLength
    && !containsAsciiControlCharacter(candidate)
    ? candidate
    : null;
}

/**
 * Restores focus to the controlled rendering (or its naturally focusable owner) after returning
 * from the terminology workspace. Static wording receives only a temporary programmatic focus
 * seam; it is never added to the page's normal tab order.
 */
export function restoreTerminologyUsageFocus(
  document: Document,
  usageId: string | null | undefined,
): boolean {
  const normalizedUsageId = normalizeTerminologyReturnUsage(usageId);
  if (!normalizedUsageId) {
    return false;
  }

  const marker = Array.from(
    document.querySelectorAll<HTMLElement>(TERMINOLOGY_CONTROLLED_SELECTOR),
  ).find((candidate) => candidate.getAttribute('data-terminology-usage') === normalizedUsageId);
  if (!marker) {
    return false;
  }

  const focusTarget = marker.matches(FOCUS_OWNER_SELECTOR)
    ? marker
    : marker.closest<HTMLElement>(FOCUS_OWNER_SELECTOR) ?? marker;
  const requiresTemporaryTabIndex = focusTarget === marker
    && !marker.matches(FOCUS_OWNER_SELECTOR);
  if (requiresTemporaryTabIndex) {
    marker.setAttribute('tabindex', '-1');
  }

  focusTarget.scrollIntoView({ block: 'nearest' });
  focusTarget.focus({ preventScroll: true });
  if (requiresTemporaryTabIndex) {
    marker.removeAttribute('tabindex');
  }
  return true;
}

function containsAsciiControlCharacter(value: string): boolean {
  return Array.from(value).some((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 0x1f || codePoint === 0x7f;
  });
}

function addQueryValue(
  queryParams: Record<string, string>,
  name: string,
  value: string | null,
  maximumLength = 120,
): void {
  const normalized = normalizeTerminologyContextValue(value, maximumLength);
  if (normalized) {
    queryParams[name] = normalized;
  }
}
